import { logger } from '@/utils/logger';
import SendOutlined from '@ant-design/icons/SendOutlined';
import App from 'antd/es/app';
import Button from 'antd/es/button';
import Divider from 'antd/es/divider';
import Empty from 'antd/es/empty';
import Input from 'antd/es/input';
import Skeleton from 'antd/es/skeleton';
import Typography from 'antd/es/typography';
import { useCallback, useState } from 'react';
import styled from 'styled-components';

const { Title, Text } = Typography;
const { TextArea } = Input;

interface Comment {
  id: string;
  content: string;
  authorName?: string;
  authorAvatar?: string;
  createdAt: string;
}

interface TaskCommentsProps {
  taskId: string;
  teamId: string;
}

const CommentsContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: ${({ theme }) => theme.token?.paddingLG || '24px'};
`;

const CommentsHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.token?.marginMD || '16px'};
`;

const CommentList = styled.div`
  margin-bottom: 0;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.token?.marginMD || '16px'};
`;

const CommentItem = styled.div`
  padding: ${({ theme }) => theme.token?.paddingMD || '16px'} 0;
`;

const CommentContent = styled.div`
  flex: 1;
`;

const CommentMeta = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.token?.marginXS || '4px'};
`;

const CommentAuthor = styled(Text)`
  font-weight: bold;
  margin-right: ${({ theme }) => theme.token?.marginLG || '24px'};
`;

const CommentDate = styled(Text)`
  color: ${({ theme }) => theme.token?.colorTextSecondary || '#8c8c8c'};
  font-size: ${({ theme }) => theme.token?.fontSizeSM || '12px'};
  position: relative;
  &:before {
    content: '';
    display: inline-block;
    width: 8px;
  }
`;

const CommentTextWrapper = styled.div`
  margin-top: ${({ theme }) => theme.token?.marginXS || '4px'};
`;

const CommentInputContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const CommentActions = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: ${({ theme }) => theme.token?.marginSM || '8px'};
`;

const StyledDivider = styled(Divider)`
  margin: ${({ theme }) => theme.token?.marginSM || '8px'} 0;
`;

const EmptyCommentsWrapper = styled.div`
  padding: ${({ theme }) => theme.token?.paddingLG || '24px'} 0;
`;

const CommentTextarea = styled(TextArea)`
  border-radius: ${({ theme }) => theme.token?.borderRadiusLG || '8px'};
`;

// Comment wrapper with divider
const CommentWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

// Increase spacing in the section containing the input
const CommentInputSection = styled.div`
  margin-top: ${({ theme }) => theme.token?.marginLG || '24px'};
  border-top: 1px solid
    ${({ theme }) => theme.token?.colorBorderSecondary || '#f0f0f0'};
  padding-top: ${({ theme }) => theme.token?.paddingMD || '16px'};
`;

export const TaskComments = ({
  taskId,
  teamId: _teamId,
}: TaskCommentsProps) => {
  const [commentText, setCommentText] = useState('');
  const [_isLoading] = useState(false);
  const [isPending, setIsPending] = useState(false);
  const { message } = App.useApp();

  // Mock comments from task state (this would normally come from the parent)
  // In a real implementation, you would pass the comments from task state
  const [comments, setComments] = useState<Comment[]>([
    {
      id: '1',
      content: 'This is a sample comment.',
      authorName: 'John Doe',
      createdAt: new Date().toISOString(),
    },
    {
      id: '2',
      content: 'Another comment for testing.',
      authorName: 'Jane Smith',
      createdAt: new Date(Date.now() - 3600000).toISOString(), // 1 hour ago
    },
  ]);

  const handleAddComment = useCallback(async () => {
    if (!commentText.trim()) return;

    try {
      setIsPending(true);

      // In a real implementation, you would call the API to add a comment
      // For now, let's just add it to our local state
      const newComment: Comment = {
        id: `temp-${Date.now()}`,
        content: commentText.trim(),
        authorName: 'Current User', // This would come from your auth context
        createdAt: new Date().toISOString(),
      };

      // Simulate API delay
      await new Promise((resolve) => setTimeout(resolve, 500));

      setComments((prev) => [newComment, ...prev]);
      setCommentText('');
      void message.success('Comment added successfully');
    } catch (error) {
      logger.error({ taskId }, 'Failed to add comment', { error });
      void message.error('Failed to add comment');
    } finally {
      setIsPending(false);
    }
  }, [commentText, message, taskId]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        void handleAddComment();
      }
    },
    [handleAddComment],
  );

  // Format date to relative time (e.g., "2 hours ago")
  const formatRelativeTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (diffInSeconds < 60) return 'just now';
    if (diffInSeconds < 3600)
      return `${Math.floor(diffInSeconds / 60)} minutes ago`;
    if (diffInSeconds < 86400)
      return `${Math.floor(diffInSeconds / 3600)} hours ago`;
    if (diffInSeconds < 604800)
      return `${Math.floor(diffInSeconds / 86400)} days ago`;

    return date.toLocaleDateString();
  };

  if (_isLoading) {
    return (
      <CommentsContainer>
        <CommentsHeader>
          <Title level={5}>Comments</Title>
        </CommentsHeader>
        <Skeleton active paragraph={{ rows: 2 }} />
        <Skeleton active paragraph={{ rows: 1 }} />
        <Skeleton active paragraph={{ rows: 3 }} />
      </CommentsContainer>
    );
  }

  return (
    <CommentsContainer>
      <CommentsHeader>
        <Title level={5}>Comments</Title>
      </CommentsHeader>

      {comments.length > 0 ? (
        <CommentList>
          {comments.map((comment, index) => (
            <CommentWrapper key={comment.id}>
              <CommentItem>
                <CommentContent>
                  <CommentMeta>
                    <CommentAuthor>
                      {comment.authorName || 'Unknown user'}
                    </CommentAuthor>
                    <CommentDate>
                      {formatRelativeTime(comment.createdAt)}
                    </CommentDate>
                  </CommentMeta>
                  <CommentTextWrapper>
                    <Text>{comment.content}</Text>
                  </CommentTextWrapper>
                </CommentContent>
              </CommentItem>
              {index < comments.length - 1 && <StyledDivider />}
            </CommentWrapper>
          ))}
        </CommentList>
      ) : (
        <EmptyCommentsWrapper>
          <Empty
            description="No comments yet"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        </EmptyCommentsWrapper>
      )}

      <CommentInputSection>
        <CommentInputContainer>
          <CommentTextarea
            placeholder="Add a comment..."
            value={commentText}
            onChange={(e) => setCommentText(e.target.value)}
            onKeyDown={handleKeyDown}
            autoSize={{ minRows: 2, maxRows: 6 }}
          />
          <CommentActions>
            <Button
              type="primary"
              icon={<SendOutlined />}
              onClick={handleAddComment}
              loading={isPending}
              disabled={!commentText.trim()}
            >
              Comment
            </Button>
          </CommentActions>
        </CommentInputContainer>
      </CommentInputSection>
    </CommentsContainer>
  );
};

export default TaskComments;
